import React from 'react';
import NewArticleSalePriceButtonProperties from "./NewArticleSalePriceButtonProperties";
import {useI18nContext} from "../../../contexts/I18nContext";
import RoundedButton from "../../../ui_components/RoundedButton/RoundedButton";
import Modal from "../../../ui_components/Modal/Modal";
import {Observer} from "mobx-react-lite";
import {useStyles} from "./NewArticleSalePriceButton.style";
import Button from "../../../ui_components/Button/Button";
import ButtonType from "../../../ui_components/Button/ButtonType";
import {useCommonContext} from "../../../contexts/CommonContext";
import ButtonSize from "../../../ui_components/Button/ButtonSize";
import TextInput from '../../../ui_components/TextInput/TextInput';
import TextInputType from '../../../ui_components/TextInput/TextInputType';
import Icon from '../../../ui_components/Icon/Icon';
import IconName from '../../../ui_components/Icon/enums/IconName';
import Access from "../../Access/AllowAccess/Access";
import {useUserContext} from "../../../contexts/UserContext";
import UpdateArticleSalePriceStore from './NewArticleSalePriceButtonStore';
import DeliveryTimeInput from '../../Inputs/DeliveryTimeInput/DeliveryTimeInput';
import ShowText from '../../Info/ShowText/ShowText';
import Checkbox from '../../../ui_components/Checkbox/Checkbox';
import UpdateChildrenArticlePrice from './UpdateChildrenArticlePrice/UpdateChildrenArticlePrice';
import UpdateArticlePriceRequest from '../../../model/articleDelivery/UpdateArticlePriceRequest';

const NewArticleSalePriceButton: React.FC<NewArticleSalePriceButtonProperties> = ({
                                                                  onClick = () => {
                                                                  },
                                                                  onSave = () => {
                                                                  },
                                                                  showIcon = true,
                                                                  showText = true,
                                                                  disabled,
                                                                  className = '',
                                                                  articleSalePriceId,
                                                                  salePriceId,
                                                                  articleId,
                                                                  articleName,
                                                                  value,
                                                                  deliveryTimeDays,
                                                                  rounded = false,
                                                                  size = ButtonSize.DEFAULT,
                                                                  type = ButtonType.DEFAULT
                                                              }) => {
    const classes = useStyles(showText);

    const i18n = useI18nContext();
    const commonContext = useCommonContext();
    const store = new UpdateArticleSalePriceStore(commonContext, salePriceId, articleSalePriceId, articleId, value, deliveryTimeDays);

    const userStore = useUserContext();
    if (!userStore.hasAccess(Access.CREATE_SALE_PRICE.valueOf())) {
        return <></>;
    }

    const handleOnclick = async () => {
        await store.openModal();
        onClick();
    }

    const onCloseModal = () => {
        store.closeModal();
    }

    const onValueChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const newVal = event.target.value;
        store.value = +newVal;
    }

    const onDeliveryTimeChange = (newDeliveryTime: number | undefined) => store.deliveryTimeDays = newDeliveryTime;

    const onApplyInAllSalePricesChange = (value: boolean) => {
        store.applyInAllSalePrices = value;
    }

    const onSalePriceValuesSave = async () => {
        const saved = await store.save();
        await store.fetchAffectedPrices();
        if (saved && store.affectedPrices.length === 0) {
            store.closeModal();
            if (onSave) {
                onSave();
            }
        }
        store.saved = true;
    }

    const handleOnClose = () => {
        store.closeModal();
        if (onSave) {
            onSave();
        }
    }

    const handleOnChildAgencyAffectedSave = async (prices: UpdateArticlePriceRequest[]) => {
        const saved = await store.saveChildAgenciesPrice(prices);
        if (saved) {
            store.closeModal();
            onSave();
        }
    }

    const text = articleSalePriceId ? "Editar precio de venta" : "Nuevo precio de venta";
    const btnIcon = articleSalePriceId ? <Icon name={IconName.EDIT_ICON}/> : <Icon name={IconName.CREATE_ICON}/>;
    const Btn = () => {
        if (rounded) {
            return <RoundedButton
                onClick={handleOnclick}
                className={className}
                disabled={disabled}
                tooltip={!showText ? text : ""}
                text={showText ? text : ""}
                type={type}
                icon={showIcon ? btnIcon : <></>}
            />
        }
        return <Button
            text={showText ? text : ""}
            className={className}
            tooltip={!showText ? text : ""}
            disabled={disabled}
            size={size}
            onClick={handleOnclick}
            type={type}
            startIcon={showIcon ? btnIcon : <></>}
        />
    }
    return <Observer>
        {() => (
            <>
                <Modal fullScreen={false} loading={store.loading} title={text}
                    onClose={onCloseModal}
                    onPressEnter={onSalePriceValuesSave}
                    open={store.isModalOpen}>
                    {!store.saved && 
                        <div className={classes.modal_container}>
                            <div className={classes.article_name_header}>
                                <ShowText text={articleName} className={classes.article_name_container}/>
                            </div>
                            <TextInput
                                label={'Valor de venta'}
                                value={store.value}
                                onChange={onValueChange}
                                type={TextInputType.NUMBER}
                                className={classes.text_input}
                                fullWidth
                            />
                            <DeliveryTimeInput value={store.deliveryTimeDays} fullWidth className={classes.input}
                                onChange={onDeliveryTimeChange}/>
                            <Checkbox text={'Aplicar en todas las listas'} onChange={onApplyInAllSalePricesChange} className={classes.input}/>
                            <Button
                                loading={store.loading} className={classes.input} onClick={onSalePriceValuesSave}
                                type={ButtonType.SECONDARY}
                                text={i18n.translate("SAVE")}/>
                        </div>
                    }
                    {store.saved && store.affectedPrices.length > 0 &&
                        <UpdateChildrenArticlePrice
                            affectedPrices={store.affectedPrices}
                            newValueDifference={store.value - value}
                            articleId={articleId}
                            salePriceId={salePriceId}
                            deliveryTimeDays={store.deliveryTimeDays}
                            onSave={handleOnChildAgencyAffectedSave}
                            onCancel={handleOnClose}
                        />}
                </Modal>
                <Btn/>
            </>
        )}
    </Observer>
}

export default NewArticleSalePriceButton;
